import { E_FloatingWidgetMobileMessageLocation } from '@/types/FloatingWidgetTrigger';
import {
  E_FloatingWidgetMessageLocation,
  E_FloatingWidgetTriggerLocation,
} from '@/types/FloatingWidgetTrigger';
import type {
  I_LoyaltyTier,
  T_CustomerLoyaltyBasicInfo,
  T_LoyaltyTier,
  T_ProgressType,
} from '@/types/RegisteredCustomer';
import { E_ProgressTypeVariable, E_TierLevellingUpMethod } from '@/types/RegisteredCustomer';

export function getMessageLocationClasses(
  triggerLocation: E_FloatingWidgetTriggerLocation,
  messageLocation: E_FloatingWidgetMessageLocation | E_FloatingWidgetMobileMessageLocation,
): string {
  switch (triggerLocation) {
    case E_FloatingWidgetTriggerLocation.BOTTOM_LEFT:
      switch (messageLocation) {
        case E_FloatingWidgetMessageLocation.RIGHT:
          return 'left-full top-1/2 -translate-y-1/2 ml-2';
        case E_FloatingWidgetMessageLocation.LEFT:
          return 'right-full top-1/2 -translate-y-1/2 mr-2';
        case E_FloatingWidgetMessageLocation.ABOVE:
        case E_FloatingWidgetMobileMessageLocation.ABOVE:
          return 'bottom-full left-0 mb-2';
        case E_FloatingWidgetMobileMessageLocation.BELOW:
          return 'top-full left-0 mt-0';
        default:
          return '';
      }

    case E_FloatingWidgetTriggerLocation.BOTTOM_RIGHT:
      switch (messageLocation) {
        case E_FloatingWidgetMessageLocation.LEFT:
          return 'right-full top-1/2 -translate-y-1/2 mr-2';
        case E_FloatingWidgetMessageLocation.RIGHT:
          return 'left-full top-1/2 -translate-y-1/2 ml-2';
        case E_FloatingWidgetMessageLocation.ABOVE:
        case E_FloatingWidgetMobileMessageLocation.ABOVE:
          return 'bottom-full right-0 mb-2';
        case E_FloatingWidgetMobileMessageLocation.BELOW:
          return 'top-full right-0 mt-0';
        default:
          return '';
      }

    default:
      return '';
  }
}

export const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const convertFalsyToZero = (falsyValue: number | string | undefined | null) =>
  Number(falsyValue ?? 0);

export const calculateTierCompletionPercentage = (
  tier: T_LoyaltyTier,
  customer: T_CustomerLoyaltyBasicInfo,
): number => {
  if (!tier) return 0;
  const { currentTier, nextTier, tierLevelingUpMethod } = tier;

  const effectiveTier = nextTier ?? currentTier;

  if (!effectiveTier || !customer) return 0;

  const amountSpentPercentage =
    (convertFalsyToZero(customer?.amountSpent) /
      convertFalsyToZero(effectiveTier?.amountSpentThreshold)) *
    100;

  const orderRatePercentage =
    (convertFalsyToZero(customer?.orderRate) /
      convertFalsyToZero(effectiveTier?.orderRateThreshold)) *
    100;

  const pointsRatePercentage =
    (convertFalsyToZero(customer?.pointsRate) /
      convertFalsyToZero(effectiveTier?.pointsRateThreshold)) *
    100;

  switch (tierLevelingUpMethod) {
    case E_TierLevellingUpMethod.AMOUNT_SPENT:
      return amountSpentPercentage;
    case E_TierLevellingUpMethod.POINTS_RATE:
      return pointsRatePercentage;
    case E_TierLevellingUpMethod.BOTH:
      return getBiggerProgress(effectiveTier, customer)?.percentage ?? 0;
    default:
      return orderRatePercentage ?? 0;
  }
};

const getBiggerProgress = (
  tier: I_LoyaltyTier,
  customer: T_CustomerLoyaltyBasicInfo | undefined,
): T_ProgressType => {
  const amountSpentPercentage =
    (convertFalsyToZero(customer?.amountSpent) / convertFalsyToZero(tier?.amountSpentThreshold)) *
    100;

  const orderRatePercentage =
    (convertFalsyToZero(customer?.orderRate) / convertFalsyToZero(tier?.orderRateThreshold)) * 100;

  if (amountSpentPercentage > orderRatePercentage) {
    return {
      percentage: amountSpentPercentage,
      variable: E_ProgressTypeVariable.AMOUNT_SPENT_PERCENTAGE,
    };
  } else {
    return {
      percentage: orderRatePercentage,
      variable: E_ProgressTypeVariable.ORDER_RATE_PERCENTAGE,
    };
  }
};
